import { Outlet, createRootRouteWithContext, createRoute, createRouter, redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { sessionQueryOptions } from '../lib/auth.js';
import { AppShell } from './shell.js';
import { AdminShell } from './AdminShell.js';
import { LoginPage } from '../features/auth/LoginPage.js';
import { AcceptInvitePage } from '../features/auth/AcceptInvitePage.js';
import { ForgotPasswordPage } from '../features/auth/ForgotPasswordPage.js';
import { ResetPasswordPage } from '../features/auth/ResetPasswordPage.js';
import { PublicWebFormPage } from '../features/public-form/PublicWebFormPage.js';
import { EmbedWebFormPage } from '../features/public-form/EmbedWebFormPage.js';
import { AccountSecurityPage } from '../features/account/AccountSecurityPage.js';
import { AccountMailboxPage } from '../features/account/AccountMailboxPage.js';
import { DashboardPage } from '../features/dashboard/DashboardPage.js';
import { LeadsListPage } from '../features/leads/LeadsListPage.js';
import { LeadDetailPage } from '../features/leads/LeadDetailPage.js';
import { NewLeadPage } from '../features/leads/NewLeadPage.js';
import { ContactsListPage } from '../features/contacts/ContactsListPage.js';
import { ContactDetailPage } from '../features/contacts/ContactDetailPage.js';
import { NewContactPage } from '../features/contacts/NewContactPage.js';
import { CompaniesListPage } from '../features/companies/CompaniesListPage.js';
import { ProspectingPage } from '../features/prospecting/ProspectingPage.js';
import { CompanyDetailPage } from '../features/companies/CompanyDetailPage.js';
import { NewCompanyPage } from '../features/companies/NewCompanyPage.js';
import { DealsPipelinePage } from '../features/deals/DealsPipelinePage.js';
import { DealDetailPage } from '../features/deals/DealDetailPage.js';
import { NewDealPage } from '../features/deals/NewDealPage.js';
import { TasksPage } from '../features/tasks/TasksPage.js';
import { AiLeadReviewPage } from '../features/ai-review/AiLeadReviewPage.js';
import { ActivitiesPage } from '../features/activities/ActivitiesPage.js';
import { ReportsPage } from '../features/reports/ReportsPage.js';
import { NotificationsPage } from '../features/notifications/NotificationsPage.js';
import { ComponentLibraryPage } from '../features/component-library/ComponentLibraryPage.js';
import { EmptyErrorStatesPage } from '../features/component-library/EmptyErrorStatesPage.js';
import { ImportPage } from '../features/imports/ImportPage.js';
import { AdminOverviewPage } from '../features/admin/AdminOverviewPage.js';
import { UsersPage } from '../features/admin/users/UsersPage.js';
import { RolesPage } from '../features/admin/roles/RolesPage.js';
import { TeamsPage } from '../features/admin/teams/TeamsPage.js';
import { LeadConfigPage } from '../features/admin/lead-config/LeadConfigPage.js';
import { PipelineConfigPage } from '../features/admin/pipelines/PipelineConfigPage.js';
import { TagsAdminPage } from '../features/admin/tags/TagsAdminPage.js';
import { CustomFieldsAdminPage } from '../features/admin/custom-fields/CustomFieldsAdminPage.js';
import { GeneralSettingsPage } from '../features/admin/settings/GeneralSettingsPage.js';
import { BrandingSettingsPage } from '../features/admin/settings/BrandingSettingsPage.js';
import { SecuritySettingsPage } from '../features/admin/settings/SecuritySettingsPage.js';
import { SsoSettingsPage } from '../features/admin/sso/SsoSettingsPage.js';
import { EmailSettingsPage } from '../features/admin/settings/EmailSettingsPage.js';
import { DataRetentionSettingsPage } from '../features/admin/settings/DataRetentionSettingsPage.js';
import { AuditLogPage } from '../features/admin/audit/AuditLogPage.js';
import { WebhooksAdminPage } from '../features/admin/webhooks/WebhooksAdminPage.js';
import { InboxPulsePage } from '../features/admin/integrations/InboxPulsePage.js';
import { CallingMeetingsPage } from '../features/admin/integrations/CallingMeetingsPage.js';
import { AiQualificationSettingsPage } from '../features/admin/integrations/AiQualificationSettingsPage.js';
import { WhatsAppSettingsPage } from '../features/admin/integrations/WhatsAppSettingsPage.js';
import { WhatsAppInboxPage } from '../features/whatsapp/WhatsAppInboxPage.js';
import { WebFormsPage } from '../features/admin/web-forms/WebFormsPage.js';
import { MobileDashboardPage } from '../features/mobile/MobileDashboardPage.js';
import { MobileLeadsPage } from '../features/mobile/MobileLeadsPage.js';
import { MobileLeadDetailPage } from '../features/mobile/MobileLeadDetailPage.js';
import { MobileMorePage } from '../features/mobile/MobileMorePage.js';

interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: z.object({ error: z.string().optional() }),
  component: LoginPage,
});

const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invite',
  validateSearch: z.object({ token: z.string().catch('') }),
  component: AcceptInvitePage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  validateSearch: z.object({ token: z.string().catch('') }),
  component: ResetPasswordPage,
});

/** Public, unauthenticated — a real visitor's browser hits this directly
 * (Admin ▸ Web forms gives out this link), branded full-page treatment.
 * UTM params live in the plain query string and are read straight off
 * `window.location` inside `DynamicLeadForm`, so no `validateSearch` typing
 * is needed here. */
const webFormRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forms/$publicKey',
  component: PublicWebFormPage,
});

/** Same form, no branded chrome — meant to sit inside an `<iframe>` on the
 * tenant's own marketing site (Admin ▸ Web forms gives out this snippet). */
const embedWebFormRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/embed/forms/$publicKey',
  component: EmbedWebFormPage,
});

const MOBILE_BREAKPOINT = 768;

/** docs/architecture/11-ui-sitemap.md's responsive-strategy table: "< 768 —
 * a different experience, not a shrunken one." `AppShell` (the desktop
 * sidebar + top bar) has no responsive layout at all, so without this a
 * phone landing on `/` or `/leads` got the desktop shell — unusably narrow,
 * and the whole reason the /m/* screens existed was invisible to a real
 * mobile visitor.
 *
 * Deliberately only these two entry points, not `/leads/$leadId` too: the
 * mobile lead-detail page's own Edit/More-actions/Convert buttons
 * intentionally `navigate` to this same desktop route (no mobile-native
 * equivalent for those flows yet) — redirecting it back to `/m/leads/:id`
 * here would immediately bounce that navigation and strand the user. `/`
 * and `/leads` are never a target of an in-app link from the mobile
 * screens, so redirecting them can't create that loop. */
function mobileEntryRedirect(pathname: string): string | null {
  if (typeof window === 'undefined' || window.innerWidth >= MOBILE_BREAKPOINT) return null;
  if (pathname === '/') return '/m';
  if (pathname === '/leads') return '/m/leads';
  return null;
}

/** A pathless layout route — everything under it requires a session
 * (docs/architecture/05 § sessions). `beforeLoad` checks the cached/fetched
 * session before rendering the shell, so a signed-out visitor never sees a
 * flash of protected UI. */
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  component: AppShell,
  beforeLoad: async ({ context, location }) => {
    try {
      await context.queryClient.ensureQueryData(sessionQueryOptions);
    } catch {
      throw redirect({ to: '/login' });
    }
    const mobileTarget = mobileEntryRedirect(location.pathname);
    if (mobileTarget) throw redirect({ to: mobileTarget });
  },
});

const dashboardRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/', component: DashboardPage });

const leadsIndexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/leads',
  validateSearch: z.object({ view: z.string().optional() }),
  component: LeadsListPage,
});
const leadsNewRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/leads/new', component: NewLeadPage });
const leadDetailRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/leads/$leadId', component: LeadDetailPage });

const contactsIndexRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/contacts', component: ContactsListPage });
const contactsNewRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/contacts/new', component: NewContactPage });
const contactDetailRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/contacts/$contactId', component: ContactDetailPage });

const companiesIndexRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/companies', component: CompaniesListPage });
const companiesNewRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/companies/new', component: NewCompanyPage });
const prospectingRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/prospecting', component: ProspectingPage });
const companyDetailRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/companies/$companyId', component: CompanyDetailPage });

const dealsIndexRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/deals', component: DealsPipelinePage });
const dealsNewRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/deals/new', component: NewDealPage });
const dealDetailRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/deals/$dealId', component: DealDetailPage });

const tasksRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/tasks', component: TasksPage });

const aiLeadReviewRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/ai-review', component: AiLeadReviewPage });

const whatsappInboxRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/whatsapp', component: WhatsAppInboxPage });

const activitiesRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/activities', component: ActivitiesPage });

const reportsRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/reports', component: ReportsPage });
const notificationsRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/notifications', component: NotificationsPage });
const componentLibraryRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/component-library', component: ComponentLibraryPage });
const emptyErrorStatesRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/component-library/states', component: EmptyErrorStatesPage });

const importRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/leads/import', component: ImportPage });
const dealImportRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/deals/import', component: () => <ImportPage entityType="opportunity" /> });

const accountSecurityRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/account/security', component: AccountSecurityPage });
const accountMailboxRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/account/mailbox',
  validateSearch: z.object({ connected: z.string().optional(), error: z.string().optional() }),
  component: AccountMailboxPage,
});

/** `/admin` is its own nested layout (sidebar sub-nav + outlet) under the
 * main app shell — docs/architecture/11-ui-sitemap.md.
 *
 * No `id` here, unlike appLayoutRoute above. An `id` marks a *pathless*
 * layout route, and the router rejects a route carrying both — it throws
 * "Route cannot have both an 'id' and a 'path' option" while the route tree is
 * built, which is before React renders anything, so the whole app was a blank
 * page. This layout does have a path: its children are declared as '/users',
 * '/roles' and so on, and they resolve under it to /admin/users, /admin/roles. */
const adminLayoutRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/admin', component: AdminShell });
const adminOverviewRoute = createRoute({ getParentRoute: () => adminLayoutRoute, path: '/', component: AdminOverviewPage });
const adminUsersRoute = createRoute({ getParentRoute: () => adminLayoutRoute, path: '/users', component: UsersPage });
const adminRolesRoute = createRoute({ getParentRoute: () => adminLayoutRoute, path: '/roles', component: RolesPage });
const adminTeamsRoute = createRoute({ getParentRoute: () => adminLayoutRoute, path: '/teams', component: TeamsPage });
const adminLeadsRoute = createRoute({ getParentRoute: () => adminLayoutRoute, path: '/leads', component: LeadConfigPage });
const adminPipelinesRoute = createRoute({ getParentRoute: () => adminLayoutRoute, path: '/pipelines', component: PipelineConfigPage });
const adminTagsRoute = createRoute({ getParentRoute: () => adminLayoutRoute, path: '/tags', component: TagsAdminPage });
const adminCustomFieldsRoute = createRoute({ getParentRoute: () => adminLayoutRoute, path: '/custom-fields', component: CustomFieldsAdminPage });
const adminGeneralRoute = createRoute({ getParentRoute: () => adminLayoutRoute, path: '/general', component: GeneralSettingsPage });
const adminBrandingRoute = createRoute({ getParentRoute: () => adminLayoutRoute, path: '/branding', component: BrandingSettingsPage });
const adminSecurityRoute = createRoute({ getParentRoute: () => adminLayoutRoute, path: '/security', component: SecuritySettingsPage });
const adminSsoRoute = createRoute({ getParentRoute: () => adminLayoutRoute, path: '/sso', component: SsoSettingsPage });
const adminEmailRoute = createRoute({ getParentRoute: () => adminLayoutRoute, path: '/email', component: EmailSettingsPage });
// Notification rules moved onto the combined Email & notifications screen
// (design/orion-crm-design/rendered-html/AdminEmail.html shows both on one
// page) — this route just forwards there instead of keeping a second,
// divergent editor for the same `notifications` settings namespace.
const adminNotificationsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/notifications',
  beforeLoad: () => {
    throw redirect({ to: '/admin/email' });
  },
});
const adminRetentionRoute = createRoute({ getParentRoute: () => adminLayoutRoute, path: '/retention', component: DataRetentionSettingsPage });
const adminAuditLogRoute = createRoute({ getParentRoute: () => adminLayoutRoute, path: '/audit-log', component: AuditLogPage });
// API keys moved onto the combined "API keys & webhooks" screen
// (design/orion-crm-design/rendered-html/AdminWebhooks.html shows both on
// one page, and the design's own nav already lists them as one entry) —
// this route just forwards there.
const adminApiKeysRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/api-keys',
  beforeLoad: () => {
    throw redirect({ to: '/admin/webhooks' });
  },
});
const adminWebhooksRoute = createRoute({ getParentRoute: () => adminLayoutRoute, path: '/webhooks', component: WebhooksAdminPage });
const adminInboxPulseRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/integrations/inbox-pulse',
  validateSearch: z.object({ connected: z.string().optional(), error: z.string().optional() }),
  component: InboxPulsePage,
});
const adminCallingMeetingsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/integrations/calling-meetings',
  validateSearch: z.object({ connected: z.string().optional(), error: z.string().optional() }),
  component: CallingMeetingsPage,
});
const adminWebFormsRoute = createRoute({ getParentRoute: () => adminLayoutRoute, path: '/web-forms', component: WebFormsPage });
const adminAiQualificationRoute = createRoute({ getParentRoute: () => adminLayoutRoute, path: '/integrations/ai-qualification', component: AiQualificationSettingsPage });
const adminWhatsAppRoute = createRoute({ getParentRoute: () => adminLayoutRoute, path: '/integrations/whatsapp', component: WhatsAppSettingsPage });

/** The mobile screens (docs/architecture/08-v1-scope.md § "Mobile-optimised
 * field views ... as a responsive PWA", docs/architecture/11-ui-sitemap.md's
 * responsive-strategy table: "< 768 — a different experience, not a
 * shrunken one") are their own route tree, session-gated the same way
 * `appLayoutRoute` is, but deliberately NOT nested under it — `AppShell`
 * always renders the desktop sidebar around its children, which is exactly
 * the chrome these screens replace with their own header + bottom tab bar. */
const mobileLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/m',
  component: () => <Outlet />,
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(sessionQueryOptions);
    } catch {
      throw redirect({ to: '/login' });
    }
  },
});
const mobileDashboardRoute = createRoute({ getParentRoute: () => mobileLayoutRoute, path: '/', component: MobileDashboardPage });
const mobileLeadsRoute = createRoute({ getParentRoute: () => mobileLayoutRoute, path: '/leads', component: MobileLeadsPage });
const mobileLeadDetailRoute = createRoute({ getParentRoute: () => mobileLayoutRoute, path: '/leads/$leadId', component: MobileLeadDetailPage });
const mobileMoreRoute = createRoute({ getParentRoute: () => mobileLayoutRoute, path: '/more', component: MobileMorePage });

const routeTree = rootRoute.addChildren([
  loginRoute,
  acceptInviteRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  webFormRoute,
  embedWebFormRoute,
  appLayoutRoute.addChildren([
    dashboardRoute,
    leadsIndexRoute,
    leadsNewRoute,
    leadDetailRoute,
    contactsIndexRoute,
    contactsNewRoute,
    contactDetailRoute,
    companiesIndexRoute,
    companiesNewRoute,
    prospectingRoute,
    companyDetailRoute,
    dealsIndexRoute,
    dealsNewRoute,
    dealImportRoute,
    dealDetailRoute,
    tasksRoute,
    aiLeadReviewRoute,
    whatsappInboxRoute,
    activitiesRoute,
    reportsRoute,
    notificationsRoute,
    componentLibraryRoute,
    emptyErrorStatesRoute,
    importRoute,
    accountSecurityRoute,
    accountMailboxRoute,
    adminLayoutRoute.addChildren([
      adminOverviewRoute,
      adminUsersRoute,
      adminRolesRoute,
      adminTeamsRoute,
      adminLeadsRoute,
      adminPipelinesRoute,
      adminTagsRoute,
      adminCustomFieldsRoute,
      adminGeneralRoute,
      adminBrandingRoute,
      adminSecurityRoute,
      adminSsoRoute,
      adminEmailRoute,
      adminNotificationsRoute,
      adminRetentionRoute,
      adminAuditLogRoute,
      adminApiKeysRoute,
      adminWebhooksRoute,
      adminWebFormsRoute,
      adminInboxPulseRoute,
      adminCallingMeetingsRoute,
      adminAiQualificationRoute,
      adminWhatsAppRoute,
    ]),
  ]),
  mobileLayoutRoute.addChildren([mobileDashboardRoute, mobileLeadsRoute, mobileLeadDetailRoute, mobileMoreRoute]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({ routeTree, context: { queryClient }, defaultPreload: 'intent' });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
